import { Injectable, inject } from '@angular/core';
import { AppActions } from '@navfleet/client/core/app';
import {
    FleetEventFacade,
    FleetEventNotificationSignalRService,
    NotificationMethods,
} from '@navfleet/client/fleet/event/domain/data-access';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { NavigationService } from '@navfleet/client/navigation/domain/data-access';
import { NAV_ROUTES } from '@navfleet/client/navigation/domain/models';
import {
    NotificationRuleData,
    NotificationRuleDeletedData,
    NotificationRuleVesselsEmailsUpdatedData,
} from '@navfleet/client/shared/models/notification';
import { filterTruthy } from '@navfleet/client/shared/util/functions';
import { TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { SettingsNotificationRuleApi } from '../api/settings-notification-rule.api';
import * as SettingsNotificationRuleActions from './settings-notification-rule.actions';
import * as fromSettingsNotificationRule from './settings-notification-rule.reducer';

@Injectable()
export class SettingsNotificationRuleEffects {
    private actions$ = inject(Actions);
    private store$ = inject<Store<fromSettingsNotificationRule.State>>(Store);
    private notificationRuleApi = inject(SettingsNotificationRuleApi);
    private notificationSignalRService = inject(FleetEventNotificationSignalRService);
    private fleetEventNotificationFacade = inject(FleetEventFacade);
    private tenantFacade = inject(TenantFacade);
    private navigationService = inject(NavigationService);
    private licenseFacade = inject(LicenseFacade);

    public readonly initNotificationRuleData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.loadAppData),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(() => [
                SettingsNotificationRuleActions.loadNotificationRuleConfig(),
                SettingsNotificationRuleActions.loadNotificationRules(),
            ])
        )
    );

    public readonly loadNotificationRuleConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.loadNotificationRuleConfig),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            switchMap(() =>
                this.notificationRuleApi.getNotificationRuleConfig().pipe(
                    map((config) => SettingsNotificationRuleActions.loadNotificationRuleConfigSuccess({ config })),
                    catchError((error) =>
                        of(SettingsNotificationRuleActions.loadNotificationRuleConfigFailure({ error }))
                    )
                )
            )
        )
    );

    public readonly loadNotificationRules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.loadNotificationRules),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            switchMap(() =>
                this.notificationRuleApi.getNotificationRules().pipe(
                    map((rules) => SettingsNotificationRuleActions.loadNotificationRulesSuccess({ rules })),
                    catchError((error) => of(SettingsNotificationRuleActions.loadNotificationRulesFailure({ error })))
                )
            )
        )
    );

    public readonly fetchNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.fetchNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id }) =>
                this.notificationRuleApi.fetchNotificationRule(id).pipe(
                    map((response) => SettingsNotificationRuleActions.fetchNotificationRuleSuccess({ rule: response })),
                    catchError((error) => of(SettingsNotificationRuleActions.fetchNotificationRuleFailure({ error })))
                )
            )
        )
    );

    public readonly createNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.createNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ rule }) =>
                this.notificationRuleApi.createNotificationRule(rule).pipe(
                    map((response) =>
                        SettingsNotificationRuleActions.createNotificationRuleSuccess({
                            rule: response,
                            navigateToOverview: true,
                        })
                    ),
                    catchError((error) => of(SettingsNotificationRuleActions.createNotificationRuleFailure({ error })))
                )
            )
        )
    );

    public readonly createNotificationRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsNotificationRuleActions.createNotificationRuleSuccess),
                this.licenseFacade.hasLicenseFilter(License.Notifications),
                filterTruthy(({ navigateToOverview }) => navigateToOverview),
                tap(() => this.navigationService.navigate(NAV_ROUTES.SETTINGS.children.NOTIFICATIONS))
            ),
        { dispatch: false }
    );

    public readonly updateNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.updateNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ rule }) =>
                this.notificationRuleApi.updateNotificationRule(rule).pipe(
                    map((response) =>
                        SettingsNotificationRuleActions.updateNotificationRuleSuccess({
                            rule: response,
                            navigateToOverview: true,
                        })
                    ),
                    catchError((error) => of(SettingsNotificationRuleActions.createNotificationRuleFailure({ error })))
                )
            )
        )
    );

    public readonly updateNotificationRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsNotificationRuleActions.updateNotificationRuleSuccess),
                this.licenseFacade.hasLicenseFilter(License.Notifications),
                filterTruthy(({ navigateToOverview }) => navigateToOverview),
                tap(() => this.navigationService.navigate(NAV_ROUTES.SETTINGS.children.NOTIFICATIONS))
            ),
        { dispatch: false }
    );

    public readonly deleteNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.deleteNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id, navigateToOverview }) =>
                this.notificationRuleApi.deleteNotificationRule(id).pipe(
                    map(() =>
                        SettingsNotificationRuleActions.deleteNotificationRuleSuccess({ id, navigateToOverview })
                    ),
                    catchError((error) => of(SettingsNotificationRuleActions.deleteNotificationRuleFailure({ error })))
                )
            )
        )
    );

    public readonly deleteNotificationRuleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsNotificationRuleActions.deleteNotificationRuleSuccess),
                this.licenseFacade.hasLicenseFilter(License.Notifications),
                tap(({ id }) => this.fleetEventNotificationFacade.removeEventNotificationsByRuleId(id)),
                filterTruthy(({ navigateToOverview }) => navigateToOverview),
                tap(() => this.navigationService.navigate(NAV_ROUTES.SETTINGS.children.NOTIFICATIONS))
            ),
        { dispatch: false }
    );

    public readonly activateNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.activateNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id }) =>
                this.notificationRuleApi.activateNotificationRule(id).pipe(
                    map((response) =>
                        SettingsNotificationRuleActions.activateNotificationRuleSuccess({ rule: response })
                    ),
                    catchError((error) =>
                        of(SettingsNotificationRuleActions.activateNotificationRuleFailure({ error }))
                    )
                )
            )
        )
    );

    public readonly deactivateNotificationRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsNotificationRuleActions.deactivateNotificationRule),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id }) =>
                this.notificationRuleApi.deactivateNotificationRule(id).pipe(
                    map((response) =>
                        SettingsNotificationRuleActions.deactivateNotificationRuleSuccess({ rule: response })
                    ),
                    catchError((error) =>
                        of(SettingsNotificationRuleActions.deactivateNotificationRuleFailure({ error }))
                    )
                )
            )
        )
    );

    public readonly signalRNotificationRuleAdded$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationRuleAdded).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([data, tenant]) => {
                    if (data.tenantId === tenant.id) {
                        this.signalRNotificationRuleAdded(data);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationRuleUpdated$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationRuleUpdated).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([data, tenant]) => {
                    if (data.tenantId === tenant.id) {
                        this.signalRNotificationRuleUpdated(data);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationRuleDeleted$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationRuleDeleted).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([data, tenant]) => {
                    if (data.tenantId === tenant.id) {
                        this.signalRNotificationRuleDeleted(data);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationRuleVesselsEmailsUpdated$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationRuleVesselsEmailsUpdated).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([data, tenant]) => {
                    const filteredData = data.filter((d) => d.tenantId === tenant.id);

                    if (filteredData.length) {
                        this.signalRNotificationRuleVesselsEmailsUpdated(filteredData);
                    }
                })
            ),
        { dispatch: false }
    );

    private signalRNotificationRuleAdded(rule: NotificationRuleData) {
        this.store$.dispatch(
            SettingsNotificationRuleActions.createNotificationRuleSuccess({ rule, navigateToOverview: false })
        );
    }

    private signalRNotificationRuleUpdated(rule: NotificationRuleData) {
        this.store$.dispatch(
            SettingsNotificationRuleActions.updateNotificationRuleSuccess({ rule, navigateToOverview: false })
        );
    }

    private signalRNotificationRuleDeleted(data: NotificationRuleDeletedData) {
        this.store$.dispatch(
            SettingsNotificationRuleActions.deleteNotificationRuleSuccess({
                id: data.ruleId,
                navigateToOverview: false,
            })
        );
    }

    private signalRNotificationRuleVesselsEmailsUpdated(data: NotificationRuleVesselsEmailsUpdatedData[]) {
        this.store$.dispatch(SettingsNotificationRuleActions.signalRVesselsEmailsUpdated({ data }));
    }
}
